import React, { useState, useEffect } from 'react';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { Calendar, ExternalLink, Copy, Mail } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { getHolidayForDate } from '@/utils/holidays';

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTE_OPTIONS = [0, 15, 30, 45];

const MeetingFinder = ({ clocks, holidays = {} }) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [meetingTitle, setMeetingTitle] = useState('Team Sync');
  const [meetingDuration, setMeetingDuration] = useState(60);
  const [meetingMinute, setMeetingMinute] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 30000);
    return () => clearInterval(timer);
  }, []);

  if (clocks.length === 0) return null;

  // Determine top-of-current-hour in Austin time, as a real UTC Date.
  // fromZonedTime correctly handles DST and avoids manual offset parsing.
  const austinHourStartUTC = (() => {
    const y = parseInt(formatInTimeZone(currentTime, 'America/Chicago', 'yyyy'), 10);
    const mo = parseInt(formatInTimeZone(currentTime, 'America/Chicago', 'M'), 10);
    const d = parseInt(formatInTimeZone(currentTime, 'America/Chicago', 'd'), 10);
    const h = parseInt(formatInTimeZone(currentTime, 'America/Chicago', 'H'), 10);
    return fromZonedTime(new Date(y, mo - 1, d, h, 0, 0), 'America/Chicago');
  })();

  // Minute fraction of how far we are into the current hour (0 to 1)
  const nowMinutes = (currentTime.getTime() - austinHourStartUTC.getTime()) / (60 * 1000);
  const nowFraction = Math.max(0, Math.min(1, nowMinutes / 60));

  const isBusinessTime = (futureTime, clock) => {
    const hour = parseInt(formatInTimeZone(futureTime, clock.timezone, 'H'), 10);
    const day = parseInt(formatInTimeZone(futureTime, clock.timezone, 'i'), 10);
    const isWeekday = day >= 1 && day <= 5;
    const start = clock.workStartHour ?? 9;
    const end = clock.workEndHour ?? 19;
    const holiday = getHolidayForDate(futureTime, clock.timezone, holidays);
    return hour >= start && hour < end && isWeekday && !holiday;
  };

  const getHoliday = (futureTime, clock) =>
    getHolidayForDate(futureTime, clock.timezone, holidays);

  const hourSlots = HOURS.map((offset) => {
    const futureTime = new Date(austinHourStartUTC.getTime() + offset * 60 * 60 * 1000);
    const slotData = clocks.map((clock) => {
      const isBusiness = isBusinessTime(futureTime, clock);
      const displayTime = formatInTimeZone(futureTime, clock.timezone, 'HH:mm');
      const day = formatInTimeZone(futureTime, clock.timezone, 'EEE');
      return { clock, isBusiness, displayTime, day };
    });
    const availableCount = slotData.filter((s) => s.isBusiness).length;
    const austinTime = formatInTimeZone(futureTime, 'America/Chicago', 'HH:mm');
    return { offset, slotData, availableCount, austinTime, futureTime };
  });

  const maxAvailable = Math.max(...hourSlots.map((s) => s.availableCount));

  const openPicker = (slot) => {
    setSelectedSlot(slot);
    setMeetingTitle('Team Sync');
    setMeetingDuration(60);
    setMeetingMinute(0);
  };

  // Selected slot's start time accounting for minute offset
  const getMeetingStart = () => {
    if (!selectedSlot) return null;
    return new Date(selectedSlot.futureTime.getTime() + meetingMinute * 60 * 1000);
  };

  const getGoogleCalendarUrl = () => {
    const start = getMeetingStart();
    if (!start) return '';
    const end = new Date(start.getTime() + meetingDuration * 60 * 1000);
    const fmt = (d) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: meetingTitle,
      dates: `${fmt(start)}/${fmt(end)}`,
      details: buildMeetingDetails(),
    });
    return `https://www.google.com/calendar/render?${params.toString()}`;
  };

  const getMailtoUrl = () => {
    const start = getMeetingStart();
    if (!start) return '';
    const gcalLink = getGoogleCalendarUrl();
    const body =
      `${buildMeetingDetails()}\n\n` +
      `Duration: ${meetingDuration} minutes\n\n` +
      `Add to Google Calendar:\n${gcalLink}`;
    const params = new URLSearchParams({
      subject: meetingTitle,
      body: body,
    });
    return `mailto:?${params.toString()}`;
  };

  const buildMeetingDetails = () => {
    if (!selectedSlot) return '';
    const start = getMeetingStart();
    const lines = [`Meeting: ${meetingTitle}`, '', 'Local times for team members:', ''];
    selectedSlot.slotData.forEach((s) => {
      const localTime = formatInTimeZone(start, s.clock.timezone, 'EEE HH:mm');
      const name = s.clock.memberName
        ? `${s.clock.memberName} (${s.clock.customName})`
        : s.clock.customName;
      const isBusiness = isBusinessTime(start, s.clock);
      const status = isBusiness ? '✓' : '⚠ off-hours';
      lines.push(`• ${name}: ${localTime} ${status}`);
    });
    return lines.join('\n');
  };

  const copyDetails = async () => {
    try {
      await navigator.clipboard.writeText(buildMeetingDetails());
      toast.success('Meeting details copied to clipboard');
    } catch (err) {
      toast.error('Could not copy. Please copy manually from the dialog.');
    }
  };

  return (
    <div className="mt-16 mb-12" data-testid="meeting-finder">
      <div className="font-mono text-xs uppercase tracking-[0.4em] text-zinc-500 mb-4">
        // meeting_finder
      </div>

      <div className="terminal-block">
        {/* Title bar */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-white/[0.08]">
          <span className="terminal-dot bg-red-500/80"></span>
          <span className="terminal-dot bg-amber-400/80"></span>
          <span className="terminal-dot bg-emerald-400/80"></span>
          <span className="ml-auto font-mono text-[10px] sm:text-xs uppercase tracking-[0.35em] text-zinc-500">
            meeting_finder.exe
          </span>
        </div>

        <div className="p-6 sm:p-8">
          <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
            <div>
              <h3 className="font-heading text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#735AE2]" />
                Next 24 hours
              </h3>
              <p className="font-mono text-xs text-zinc-500 mt-1.5">
                <span className="text-[#735AE2]">{'>'}</span> click any hour to schedule · per-member working hours apply
              </p>
            </div>
            <div className="flex items-center gap-4 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              <span className="flex items-center gap-1.5">
                <div className="w-3 h-3 bg-[#735AE2]"></div>
                business
              </span>
              <span className="flex items-center gap-1.5">
                <div className="w-3 h-3 bg-white/10"></div>
                off / weekend
              </span>
            </div>
          </div>

          <div className="overflow-x-auto pb-2">
            <div className="min-w-[800px] relative">
              {/* Now indicator */}
              <div
                className="absolute top-0 bottom-0 pointer-events-none z-20"
                style={{
                  left: `calc(160px + 4px + (100% - 160px - 4px) * ${nowFraction} / 24)`,
                }}
                data-testid="now-indicator"
              >
                <div className="relative h-full">
                  <div className="absolute top-0 -translate-x-1/2 -translate-y-1 z-30">
                    <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-emerald-400 font-bold bg-[#050505] px-1.5 py-0.5 border border-emerald-400/50">
                      NOW
                    </div>
                  </div>
                  <div className="w-px h-full bg-emerald-400/80 -translate-x-1/2"></div>
                </div>
              </div>

              {/* Austin time header */}
              <div className="grid grid-cols-[160px_repeat(24,minmax(36px,1fr))] gap-1 mb-1 pt-5">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#735AE2] font-semibold flex items-center pl-2">
                  Austin, TX
                </div>
                {hourSlots.map((slot) => (
                  <button
                    key={slot.offset}
                    onClick={() => openPicker(slot)}
                    className="font-mono text-[10px] text-center text-zinc-500 py-1 hover:text-[#9D8AEC] hover:bg-white/5 transition-colors cursor-pointer"
                    data-testid={`meeting-slot-${slot.offset}`}
                    title="Click to schedule a meeting at this time"
                  >
                    {slot.austinTime}
                  </button>
                ))}
              </div>

              {/* Availability count row */}
              <div className="grid grid-cols-[160px_repeat(24,minmax(36px,1fr))] gap-1 mb-3 pb-3 border-b border-white/[0.06]">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 flex items-center pl-2">
                  Available
                </div>
                {hourSlots.map((slot) => (
                  <button
                    key={slot.offset}
                    onClick={() => openPicker(slot)}
                    className={`font-mono text-xs font-bold text-center py-1 cursor-pointer hover:bg-[#735AE2]/20 transition-colors ${
                      slot.availableCount === maxAvailable && maxAvailable > 0
                        ? 'text-[#9D8AEC] bg-[#735AE2]/10'
                        : 'text-zinc-400'
                    }`}
                    data-testid={`availability-${slot.offset}`}
                  >
                    {slot.availableCount}
                  </button>
                ))}
              </div>

            {/* One row per clock */}
            {clocks.map((clock) => (
              <div
                key={clock.id}
                className="grid grid-cols-[160px_repeat(24,minmax(36px,1fr))] gap-1 mb-1"
              >
                <div className="pl-2 flex items-center gap-2 min-w-0">
                  {clock.avatar ? (
                    <img
                      src={clock.avatar}
                      alt={clock.memberName || clock.customName}
                      className="w-7 h-7 object-cover border border-white/[0.12] shrink-0"
                    />
                  ) : (
                    <div className="w-7 h-7 bg-white/[0.02] border border-white/[0.12] flex items-center justify-center shrink-0">
                      <span className="font-mono text-[9px] font-bold text-[#9D8AEC]">
                        {(clock.memberName || clock.customName).slice(0, 2).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div className="flex flex-col justify-center min-w-0 flex-1">
                    {clock.memberName && (
                      <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#735AE2] font-semibold truncate">
                        {clock.memberName}
                      </div>
                    )}
                    <div className="font-body text-xs font-semibold text-white truncate" title={clock.customName}>
                      {clock.customName}
                    </div>
                  </div>
                </div>
                {hourSlots.map((slot) => {
                  const data = slot.slotData.find((s) => s.clock.id === clock.id);
                  return (
                    <button
                      key={slot.offset}
                      onClick={() => openPicker(slot)}
                      className={`h-7 transition-all hover:ring-1 hover:ring-[#735AE2] cursor-pointer ${
                        data?.isBusiness
                          ? 'bg-[#735AE2]'
                          : 'bg-white/[0.04]'
                      }`}
                      title={`${data?.day} ${data?.displayTime} - ${data?.isBusiness ? 'In office' : 'Off hours'}`}
                    ></button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-white/[0.06]">
          <div className="flex items-center gap-2 font-mono text-xs text-zinc-500">
            <span className="text-[#735AE2]">{'>'}</span>
            <span>
              best window: {maxAvailable} of {clocks.length} team members available simultaneously
            </span>
          </div>
        </div>
        </div>
      </div>

      {/* Meeting Picker Dialog */}
      <Dialog open={!!selectedSlot} onOpenChange={(open) => !open && setSelectedSlot(null)}>
        <DialogContent
          className="bg-[#0A0A0A] border border-white/[0.12] text-white max-w-2xl max-h-[90vh] overflow-y-auto rounded-none"
          data-testid="meeting-picker-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-bold text-white flex items-center gap-2">
              <Calendar className="w-6 h-6 text-[#735AE2]" />
              Schedule meeting
            </DialogTitle>
            <DialogDescription className="font-mono text-xs text-zinc-400 uppercase tracking-widest">
              <span className="text-[#735AE2]">{'>'}</span>{' '}
              {selectedSlot &&
                formatInTimeZone(getMeetingStart(), 'America/Chicago', 'EEEE, MMM d · HH:mm') +
                  ' austin'}
            </DialogDescription>
          </DialogHeader>

          {selectedSlot && (
            <div className="space-y-5 mt-4">
              <div className="space-y-2">
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">Meeting title</label>
                <Input
                  value={meetingTitle}
                  onChange={(e) => setMeetingTitle(e.target.value)}
                  className="bg-white/[0.03] border-white/[0.15] text-white font-body rounded-none focus:border-[#735AE2] focus-visible:ring-0"
                  data-testid="meeting-title-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">Start at</label>
                  <div className="flex gap-1">
                    {MINUTE_OPTIONS.map((mins) => (
                      <button
                        key={mins}
                        onClick={() => setMeetingMinute(mins)}
                        className={`flex-1 px-2 py-2 font-mono text-sm font-medium transition-all ${
                          meetingMinute === mins
                            ? 'bg-[#735AE2] text-white'
                            : 'bg-transparent border border-white/[0.15] text-zinc-300 hover:bg-white/[0.05]'
                        }`}
                        data-testid={`minute-${mins}`}
                      >
                        :{mins.toString().padStart(2, '0')}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">Duration</label>
                  <div className="flex gap-1 flex-wrap">
                    {[30, 60, 90, 120].map((mins) => (
                      <button
                        key={mins}
                        onClick={() => setMeetingDuration(mins)}
                        className={`flex-1 px-2 py-2 font-mono text-sm font-medium transition-all ${
                          meetingDuration === mins
                            ? 'bg-[#735AE2] text-white'
                            : 'bg-transparent border border-white/[0.15] text-zinc-300 hover:bg-white/[0.05]'
                        }`}
                        data-testid={`duration-${mins}`}
                      >
                        {mins < 60 ? `${mins}m` : mins === 60 ? '1h' : `${mins / 60}h`}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">Local times for each team member</label>
                <div className="bg-black/30 border border-white/[0.08] p-4 space-y-2 max-h-64 overflow-y-auto">
                  {selectedSlot.slotData.map((s) => {
                    const meetingStart = getMeetingStart();
                    const localTime = formatInTimeZone(meetingStart, s.clock.timezone, 'HH:mm');
                    const localDay = formatInTimeZone(meetingStart, s.clock.timezone, 'EEE');
                    const inBusiness = isBusinessTime(meetingStart, s.clock);
                    const holiday = getHoliday(meetingStart, s.clock);
                    return (
                      <div
                        key={s.clock.id}
                        className="flex items-center justify-between gap-3 py-2 border-b border-white/[0.06] last:border-b-0"
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          {s.clock.avatar ? (
                            <img
                              src={s.clock.avatar}
                              alt={s.clock.memberName || s.clock.customName}
                              className="w-9 h-9 object-cover border border-white/[0.12] shrink-0"
                            />
                          ) : (
                            <div className="w-9 h-9 bg-white/[0.02] border border-white/[0.12] flex items-center justify-center shrink-0">
                              <span className="font-mono text-[10px] font-bold text-[#9D8AEC]">
                                {(s.clock.memberName || s.clock.customName).slice(0, 2).toUpperCase()}
                              </span>
                            </div>
                          )}
                          <div className="min-w-0">
                            {s.clock.memberName && (
                              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#735AE2] font-semibold">
                                {s.clock.memberName}
                              </div>
                            )}
                            <div className="font-body text-sm text-white truncate">{s.clock.customName}</div>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="font-mono text-base text-white font-bold tabular-nums">
                            {localDay} {localTime}
                          </div>
                          <div
                            className={`font-mono text-[10px] uppercase tracking-[0.2em] font-semibold ${
                              holiday
                                ? 'text-rose-300'
                                : inBusiness
                                  ? 'text-emerald-400'
                                  : 'text-amber-400'
                            }`}
                            title={holiday ? holiday.name : ''}
                          >
                            {holiday
                              ? `⚑ ${holiday.name}`
                              : inBusiness
                                ? '✓ in office'
                                : '⚠ off hours'}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-2">
                <a
                  href={getGoogleCalendarUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="google-calendar-link"
                >
                  <Button className="w-full bg-[#735AE2] text-white hover:bg-[#8A75EA] rounded-none font-mono text-xs uppercase tracking-widest font-medium">
                    <ExternalLink className="w-4 h-4 mr-1.5" />
                    Calendar
                  </Button>
                </a>
                <a href={getMailtoUrl()} data-testid="email-invite-link">
                  <Button className="w-full bg-transparent border border-[#735AE2]/60 text-[#9D8AEC] hover:bg-[#735AE2]/10 hover:text-white rounded-none font-mono text-xs uppercase tracking-widest font-medium">
                    <Mail className="w-4 h-4 mr-1.5" />
                    Send invite
                  </Button>
                </a>
                <Button
                  onClick={copyDetails}
                  variant="outline"
                  className="bg-transparent border border-white/[0.2] text-white hover:bg-white/[0.05] rounded-none font-mono text-xs uppercase tracking-widest font-medium"
                  data-testid="copy-meeting-details"
                >
                  <Copy className="w-4 h-4 mr-1.5" />
                  Copy
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MeetingFinder;
